import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { prizeService, Prize } from '../../services/prize.service';
import { USER_ROLES } from '../../constants';
import { toast } from '../../components/Toast';
import styles from './PrizeManagement.module.css';

export const PrizeManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPrize, setEditingPrize] = useState<Prize | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pointsCost: 0,
    sortOrder: 0,
  });

  // Memoize initial form data
  const initialFormData = useMemo(
    () => ({
      name: '',
      description: '',
      pointsCost: 0,
      sortOrder: 0,
    }),
    []
  );

  // Carica lista premi
  const loadPrizes = useCallback(async () => {
    if (!currentUser?.companyId) return;

    setLoading(true);
    setError('');

    try {
      const data = await prizeService.getPrizes(currentUser.companyId);
      setPrizes(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento dei premi';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.companyId]);

  useEffect(() => {
    loadPrizes();
  }, [loadPrizes]);

  // Memoize openCreateModal
  const openCreateModal = useCallback(() => {
    setEditingPrize(null);
    setFormData(initialFormData);
    setShowModal(true);
  }, [initialFormData]);

  // Memoize openEditModal
  const openEditModal = useCallback((prize: Prize) => {
    setEditingPrize(prize);
    setFormData({
      name: prize.name,
      description: prize.description || '',
      pointsCost: prize.pointsCost,
      sortOrder: prize.sortOrder,
    });
    setShowModal(true);
  }, []);

  // Memoize closeModal
  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingPrize(null);
    setFormData(initialFormData);
    setError('');
  }, [initialFormData]);

  // Memoize handleSubmit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      try {
        if (editingPrize) {
          await prizeService.updatePrize(editingPrize.id, {
            name: formData.name,
            description: formData.description || undefined,
            pointsCost: formData.pointsCost,
            sortOrder: formData.sortOrder,
          });
          toast.success('Premio aggiornato con successo!');
        } else {
          await prizeService.createPrize({
            name: formData.name,
            description: formData.description || undefined,
            pointsCost: formData.pointsCost,
            sortOrder: formData.sortOrder,
          });
          toast.success('Premio creato con successo!');
        }
        closeModal();
        loadPrizes();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Errore nel salvataggio del premio';
        setError(message);
      }
    },
    [editingPrize, formData, closeModal, loadPrizes]
  );

  // Memoize handleDelete
  const handleDelete = useCallback(
    async (id: number, name: string) => {
      if (!window.confirm(`Sei sicuro di voler eliminare il premio "${name}"?`)) {
        return;
      }

      try {
        await prizeService.deletePrize(id);
        toast.success('Premio eliminato con successo');
        loadPrizes();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Errore nell\'eliminazione del premio';
        toast.error(message);
      }
    },
    [loadPrizes]
  );

  // Verifica accesso: solo Owner e SuperAdmin
  if (!currentUser || currentUser.role > USER_ROLES.OWNER) {
    return (
      <div className={styles.prizeManagement}>
        <div className={styles.error}>
          Accesso non autorizzato. Solo Owner e Super Admin possono gestire i premi.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.prizeManagement}>
      <div className={styles.header}>
        <h1>🎁 Gestione Premi</h1>
        <button className={styles.createButton} onClick={openCreateModal}>
          ➕ Nuovo Premio
        </button>
      </div>

      {error && (
        <div className={styles.error}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Caricamento premi...</div>
      ) : prizes.length === 0 ? (
        <div className={styles.empty}>
          <p>📭 Nessun premio trovato. Crea il primo premio per il catalogo fedeltà!</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Descrizione</th>
                <th>Costo Punti</th>
                <th>Ordine</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {prizes.map((prize) => (
                <tr key={prize.id}>
                  <td className={styles.prizeName}>{prize.name}</td>
                  <td className={styles.description}>{prize.description || '-'}</td>
                  <td className={styles.center}>{prize.pointsCost}</td>
                  <td className={styles.center}>{prize.sortOrder}</td>
                  <td>
                    <span className={prize.isActive ? styles.badgeActive : styles.badgeInactive}>
                      {prize.isActive ? '✓ Attivo' : '✗ Disattivato'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.editButton}
                        onClick={() => openEditModal(prize)}
                        title="Modifica"
                      >
                        ✏️
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(prize.id, prize.name)}
                        title="Elimina"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal per crea/modifica */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingPrize ? 'Modifica Premio' : 'Nuovo Premio'}</h2>
              <button className={styles.closeButton} onClick={closeModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="name">Nome Premio *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">Descrizione</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="pointsCost">Costo Punti *</label>
                  <input
                    type="number"
                    id="pointsCost"
                    value={formData.pointsCost}
                    onChange={(e) => setFormData({ ...formData, pointsCost: parseInt(e.target.value) || 0 })}
                    className={styles.input}
                    min={1}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="sortOrder">Ordine</label>
                  <input
                    type="number"
                    id="sortOrder"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                    className={styles.input}
                    min={0}
                  />
                </div>
              </div>

              {error && <div className={styles.error}>❌ {error}</div>}

              <button type="submit" className={styles.submitButton}>
                {editingPrize ? '💾 Salva Modifiche' : '➕ Crea Premio'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

PrizeManagement.displayName = 'PrizeManagement';
